import { PencilSquareIcon } from "@heroicons/react/24/solid";
import { zodResolver } from "@hookform/resolvers/zod";
import { Avatar } from "@moe/oss/ui/avatar";
import { Button } from "@moe/oss/ui/button";
import { Checkbox } from "@moe/oss/ui/checkbox";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@moe/oss/ui/form";
import { Input } from "@moe/oss/ui/input";
import { Textarea } from "@moe/oss/ui/textarea";
import { supportedImageExtsWithDot } from "@moe/oss/utils/utils";
import { Persona, personaConfig } from "@moe/priv/model/persona";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";

const schema = z.object({
  name: z
    .string()
    .min(personaConfig.nameMinChars, `Name must be at least ${personaConfig.nameMinChars} character`)
    .max(personaConfig.nameMaxChars, `Name must be ${personaConfig.nameMaxChars} characters or less`),
  description: z
    .string()
    .min(
      personaConfig.descriptionMinChars,
      `Description must be at least ${personaConfig.descriptionMinChars} characters`
    )
    .max(
      personaConfig.descriptionMaxChars,
      `Description must be ${personaConfig.descriptionMaxChars} characters or less`
    )
    .optional()
    .default(""),
  isDefault: z.boolean().default(false),
  avatar: z
    .instanceof(File, { message: "Avatar is required" })
    .refine(
      (file) => file.size <= personaConfig.avatarMaxFileSizeBytes,
      `Avatar file size must be less than ${personaConfig.avatarMaxFileSizeBytes / 1e6}MB`
    )
    .optional(),
  banner: z
    .instanceof(File, { message: "Banner file is invalid" })
    .refine(
      (file) => file.size <= personaConfig.bannerMaxFileSizeBytes,
      `Banner file size must be less than ${personaConfig.bannerMaxFileSizeBytes / 1e6}MB`
    )
    .optional()
});

type Schema = z.infer<typeof schema>;

interface Props {
  title: string;
  persona?: Persona;
  submit: {
    label: string;
    handle: (data: Schema) => void;
  };
  remove?: {
    label: string;
    handle: () => void;
  };
}

export function PersonaFormModal({ title, persona, submit, remove }: Props) {
  const [avatarInputRef, setAvatarInputRef] = useState<HTMLInputElement | null>(null);
  const [avatarDisplayImage, setAvatarDisplayImage] = useState<string | null>();

  const form = useForm<Schema>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: "",
      description: "",
      isDefault: false
    }
  });

  const description = form.watch("description");

  const initialData = useMemo(() => {
    if (persona) {
      return {
        name: persona.name,
        description: persona.description,
        isDefault: persona.isDefault
      };
    }
    return {
      name: "",
      description: "",
      isDefault: false
    };
  }, [persona]);

  useEffect(() => {
    form.reset(initialData);
    setAvatarDisplayImage(persona?.avatar?.toString?.() ?? null);
  }, [form, initialData, persona?.avatar]);

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.size > personaConfig.avatarMaxFileSizeBytes) {
        toast.error(`Avatar file size must be less than ${personaConfig.avatarMaxFileSizeBytes / 1e6}MB`);
        return;
      }
      form.setValue("avatar", file);
      setAvatarDisplayImage(URL.createObjectURL(file));
    }
    event.target.value = "";
  };

  return (
    <div className="border-line bg-background flex w-full flex-col space-y-5 rounded-lg border p-6 focus:outline-none md:w-96">
      <h3 className="text-tx-primary pb-2 text-lg font-semibold">{title}</h3>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(submit.handle)} className="space-y-6">
          <div className="flex w-full items-center space-x-4">
            {/* Avatar Upload */}
            <FormField
              control={form.control}
              name="avatar"
              render={({ field: { ref, onChange, value, ...field } }) => (
                <FormItem>
                  <FormControl>
                    <div
                      className="bg-action-tertiary relative flex h-16 w-16 shrink-0 cursor-pointer items-center justify-center overflow-hidden rounded-full"
                      onClick={() => avatarInputRef?.click()}
                    >
                      {avatarDisplayImage ? (
                        <Avatar src={avatarDisplayImage} className="h-full w-full object-cover" />
                      ) : (
                        <PencilSquareIcon className="text-tx-primary h-5 w-5" />
                      )}
                      <div className="absolute inset-0 bg-black opacity-0 transition-opacity duration-200 hover:opacity-10"></div>
                      <input
                        type="file"
                        className="hidden"
                        ref={(node) => {
                          ref(node);
                          setAvatarInputRef(node);
                        }}
                        onChange={handleImageChange}
                        accept={supportedImageExtsWithDot.join(",")}
                        {...field}
                      />
                    </div>
                  </FormControl>
                </FormItem>
              )}
            />

            {/* Name Input */}
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormControl>
                    <Input
                      className="border-line h-12 w-full select-text rounded-md border px-2.5"
                      placeholder="Name"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          {/* Description Input */}
          <FormField
            control={form.control}
            name="description"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Textarea
                    placeholder="Description"
                    className="scroll-secondary border-line bg-input-primary text-tx-primary flex h-36 w-full resize-none items-start border p-2.5 placeholder:font-[450] focus:outline-none"
                    data-gramm="false"
                    {...field}
                  />
                </FormControl>
                <FormLabel className="text-tx-secondary flex w-full items-center justify-end">
                  <span className="text-tx-tertiary font-semibold">
                    Token Count: {Math.ceil(description.length / 4)}
                  </span>
                </FormLabel>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Footer Controls */}
          <div className="flex w-full items-center justify-between pt-6">
            {/* Is Default Checkbox */}
            <FormField
              control={form.control}
              name="isDefault"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center space-x-2">
                  <div className="flex h-6 items-center">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        className="h-5 w-5 rounded-[4px] border-[1px]"
                      />
                    </FormControl>
                  </div>
                  <div className="flex items-center">
                    <FormLabel className="text-tx-secondary pb-2 text-sm leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                      Make Default
                    </FormLabel>
                  </div>
                </FormItem>
              )}
            />

            {/* Action Buttons */}
            <div className="flex flex-row space-x-1 text-sm">
              {remove && (
                <Button type="button" variant="clear" className="" onClick={remove.handle}>
                  {remove.label}
                </Button>
              )}
              <Button type="submit" className="">
                {submit.label}
              </Button>
            </div>
          </div>
        </form>
      </Form>
    </div>
  );
}
